import React, { useEffect, useMemo, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import UserFloorRow from "./UserFloorRow";
import { userHeat } from "../userFloorHelpers";

function RowSkeleton() {
  return (
    <div className="au-user-row-skeleton" aria-hidden>
      <span className="au-user-row-skeleton-heat" />
      <span className="au-user-row-skeleton-rank" />
      <span className="au-user-row-skeleton-body">
        <span className="au-user-row-skeleton-line au-user-row-skeleton-line-lg" />
        <span className="au-user-row-skeleton-line" />
      </span>
    </div>
  );
}

export default function UsersFloorIndex({
  users,
  selectedId,
  onSelect,
  desk,
  totalLoaded,
  loading,
  pagination,
}) {
  const listRef = useRef(null);
  const selectedRef = useRef(null);

  const maxCash = useMemo(() => {
    if (!users?.length) return 1;
    return Math.max(1, ...users.map((u) => Number(u?.CASH) || 0));
  }, [users]);

  useEffect(() => {
    if (selectedRef.current && listRef.current) {
      const r = selectedRef.current.getBoundingClientRect();
      const c = listRef.current.getBoundingClientRect();
      if (r.top < c.top || r.bottom > c.bottom) {
        selectedRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  }, [selectedId]);

  const deskLabel =
    desk === "banned" ? " · banned review" : desk === "staff" ? " · staff roster" : "";

  const { totalPages, currentPage, startIndex, endIndex, totalUsers, onPageChange } = pagination || {};

  return (
    <aside className="amf-index au-user-index" aria-label="User directory">
      <div className="amf-index-head" aria-live="polite">
        <div className="amf-index-head-row">
          <span className="amf-index-brand-label">Account ledger</span>
          <span className="amf-index-count">
            <strong>{users?.length || 0}</strong>
            {users?.length !== totalLoaded ? (
              <>
                {" / "}
                <strong>{totalLoaded}</strong>
              </>
            ) : null}
            {deskLabel}
          </span>
        </div>
      </div>

      <div className="amf-index-list" ref={listRef}>
        {loading ? (
          Array.from({ length: 8 }).map((_, i) => <RowSkeleton key={i} />)
        ) : !users?.length ? (
          <div className="amf-index-empty">No users match current filters.</div>
        ) : (
          users.map((user, idx) => {
            const id = user._id;
            const heatPct = userHeat(user, maxCash);
            return (
              <div key={id} ref={id === selectedId ? selectedRef : null}>
                <UserFloorRow
                  user={user}
                  rank={startIndex != null ? startIndex + idx + 1 : idx + 1}
                  selected={id === selectedId}
                  onSelect={() => onSelect(user)}
                  heatPct={heatPct}
                />
              </div>
            );
          })
        )}
      </div>

      {totalPages > 1 && onPageChange ? (
        <div className="au-user-index-foot">
          <span className="au-user-index-foot-meta">
            {totalUsers === 0
              ? "No users"
              : `${startIndex + 1}–${Math.min(endIndex, totalUsers)} of ${totalUsers}`}
          </span>
          <div className="au-user-index-pager">
            <button
              type="button"
              className="au-user-pager-btn"
              disabled={currentPage === 1}
              onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
              aria-label="Previous page"
            >
              <ChevronLeft size={14} aria-hidden />
            </button>
            <span className="au-user-pager-label">
              {currentPage} / {totalPages}
            </span>
            <button
              type="button"
              className="au-user-pager-btn"
              disabled={currentPage === totalPages}
              onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
              aria-label="Next page"
            >
              <ChevronRight size={14} aria-hidden />
            </button>
          </div>
        </div>
      ) : null}
    </aside>
  );
}
